// src/components/layout/Header.jsx

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  Bell,
  Wifi,
  WifiOff,
  User,
  Settings,
  LogOut,
  ChevronDown,
  AlertCircle,
  LayoutDashboard,
  MapPin,
  PlayCircle,
  Fuel,
  Cpu,
  Ship,
  PlusCircle,
  Building2,
  Users,
  FileText,
  Activity,
} from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';

import { useStore } from '../../store/useStore';
import { usePageTitle } from '../../context/PageTitleContext';

const Header = () => {
  const { user, wsConnected, logout, anomalies } = useStore();

  const { title, subtitle } = usePageTitle();

  const location = useLocation();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);

  const unreadCount = anomalies.filter((a) => !a.read).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const getPageIcon = () => {
    const path = location.pathname;

    const iconProps = {
      size: 16,
      className: 'text-primary-600',
      strokeWidth: 1.8,
    };

    if (path.includes('/dashboard'))
      return <LayoutDashboard {...iconProps} />;

    if (path.includes('/map'))
      return <MapPin {...iconProps} />;

    if (path.includes('/playback'))
      return <PlayCircle {...iconProps} />;

    if (path.includes('/fuel'))
      return <Fuel {...iconProps} />;

    if (path.includes('/sensors'))
      return <Cpu {...iconProps} />;

    if (path.includes('/vessels/register'))
      return <PlusCircle {...iconProps} />;

    if (path.includes('/vessels'))
      return <Ship {...iconProps} />;

    if (path.includes('/companies'))
      return <Building2 {...iconProps} />;

    if (path.includes('/users'))
      return <Users {...iconProps} />;

    if (path.includes('/reports'))
      return <FileText {...iconProps} />;

    if (path.includes('/settings'))
      return <Settings {...iconProps} />;

    return <Activity {...iconProps} />;
  };

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800">
      <div className="h-14 px-3 lg:px-4 flex items-center justify-between">
        {/* LEFT */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
            {getPageIcon()}
          </div>

          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-surface-900 dark:text-white truncate">
              {title}
            </h1>

            <p className="hidden md:block text-[11px] text-surface-500 dark:text-surface-400 truncate">
              {subtitle}
            </p>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-1.5">
          {/* CONNECTION */}
          <div
            className={`
              flex items-center gap-1 px-2 h-8
              border
              ${
                wsConnected
                  ? 'border-success-200 dark:border-success-900 bg-success-50 dark:bg-success-900/10'
                  : 'border-danger-200 dark:border-danger-900 bg-danger-50 dark:bg-danger-900/10'
              }
            `}
          >
            {wsConnected ? (
              <Wifi size={12} className="text-success-600" />
            ) : (
              <WifiOff size={12} className="text-danger-600" />
            )}

            <span
              className={`
                text-[10px] font-semibold
                ${
                  wsConnected
                    ? 'text-success-700'
                    : 'text-danger-700'
                }
              `}
            >
              {wsConnected ? 'LIVE' : 'OFF'}
            </span>
          </div>

          {/* NOTIFICATION */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() =>
                setShowNotifications(!showNotifications)
              }
              className="relative flex items-center justify-center w-8 h-8 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <Bell
                size={16}
                className="text-surface-600 dark:text-surface-400"
              />

              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 bg-danger-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-2 w-72 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-xl overflow-hidden z-50"
                >
                  <div className="h-10 px-3 border-b border-surface-200 dark:border-surface-800 flex items-center">
                    <h3 className="text-[11px] font-semibold text-surface-700 dark:text-surface-300">
                      Notifications
                    </h3>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {anomalies.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell
                          size={20}
                          className="mx-auto text-surface-400 mb-2"
                        />

                        <p className="text-[11px] text-surface-500">
                          No notifications
                        </p>
                      </div>
                    ) : (
                      anomalies.slice(0, 5).map((anomaly, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-3 border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors cursor-pointer"
                        >
                          <div className="flex items-start gap-2">
                            <AlertCircle
                              size={14}
                              className={`mt-0.5 shrink-0 ${
                                anomaly.severity === 'critical'
                                  ? 'text-danger-500'
                                  : anomaly.severity === 'warning'
                                  ? 'text-warning-500'
                                  : 'text-primary-500'
                              }`}
                            />

                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-medium text-surface-900 dark:text-white leading-snug">
                                {anomaly.message}
                              </p>

                              <p className="text-[10px] text-surface-500 mt-1">
                                {anomaly.vesselName}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="h-9 px-3 border-t border-surface-200 dark:border-surface-800 flex items-center">
                    <Link
                      to="/fuel"
                      onClick={() =>
                        setShowNotifications(false)
                      }
                      className="text-[11px] text-primary-600 hover:underline"
                    >
                      View all
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* USER */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 h-8 px-2 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <div className="w-6 h-6 bg-primary-600 flex items-center justify-center shrink-0">
                <User size={12} className="text-white" />
              </div>

              <span className="hidden md:block text-[12px] font-medium text-surface-900 dark:text-white">
                {user?.fullName?.split(' ')[0] || 'User'}
              </span>

              <ChevronDown
                size={12}
                className="hidden md:block text-surface-500"
              />
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-2 w-52 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-xl overflow-hidden z-50"
                >
                  <div className="px-3 py-3 border-b border-surface-200 dark:border-surface-800">
                    <p className="text-[12px] font-semibold text-surface-900 dark:text-white truncate">
                      {user?.fullName}
                    </p>

                    <p className="text-[10px] text-surface-500 truncate mt-1">
                      {user?.email}
                    </p>
                  </div>

                  <div className="py-1">
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-3 h-9 text-[12px] text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Settings size={14} />
                      Settings
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 h-9 text-[12px] text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/10 transition-colors"
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;